import { log } from 'src/app/utils/logging';
import type { Tealium } from 'src/types/appState';

import {
  PAGE_EDIT,
  PAGE_MULTI_LOCATION,
  CLASSIFY_CONTINUE,
  PAGE_CLASSIFY,
  PAGE_PAY_AND_POST,
  PAGE_CONCIERGE,
  PAGE_GUARANTEED_HIRE,
  PAGE_PREMIUM_PLUS,
  PAGE_SELECT_AD,
  PAGE_WRITE,
  SELECT_AD_CONTINUE,
  PAGE_EDIT_PAY_AND_POST,
  PAGE_MANAGE,
  WRITE_CONTINUE,
  PREMIUM_UPSELL_PRESSED,
  CONTENT_ELEMENT_PRESSED,
  MANAGE_CONTINUE,
  PAGE_SUCCESS,
  PAGE_REFRESH,
  PAGE_REFRESH_ERROR,
  PAGE_PAY_AND_POST_V2,
} from '../schema/constants';
import getPremiumUpsellProperties from '../schema/event/checkOut/premiumUpsell';
import getClassifyContinueProperties from '../schema/event/classify/classifyContinue';
import getContentElementPressedProperties from '../schema/event/contentElementPressed';
import getManagePageContinueProperties from '../schema/event/manage/manageContinue';
import getSelectPageContinueProperties from '../schema/event/selectAd/selectAdContinue';
import getWritePageContinueProperties from '../schema/event/write/writeContinue';
import getGlobalAttributes from '../schema/global';
import getCheckoutProperties, {
  eventName as CheckoutEventName,
} from '../schema/page/checkout';
import getClassifyProperties, {
  eventName as ClassifyEventName,
} from '../schema/page/classify';
import getEditProperties, {
  eventName as EditPageEventName,
} from '../schema/page/edit';
import getEditPayAndPostProperties, {
  eventName as EditPayAndPostEventName,
} from '../schema/page/editPayAndPost';
import getBookingFormProperties, {
  conciergeEventName,
  guaranteedHireEventName,
  premiumPlusEventName,
} from '../schema/page/jobAdBooking';
import getManageProperties, {
  eventName as ManageEventName,
} from '../schema/page/manage';
import getMultilocationProperties, {
  eventName as MultilocationEventName,
} from '../schema/page/multiLocation';
import getRefreshProperties, {
  eventName as RefreshEventName,
} from '../schema/page/refresh';
import getRefreshErrorProperties, {
  eventName as RefreshErrorEventName,
} from '../schema/page/refreshError';
import getSelectAdProperties, {
  eventName as SelectAdEventName,
} from '../schema/page/selectAd';
import getSuccessProperties, {
  eventName as SuccessEventName,
} from '../schema/page/success';
import getWriteProperties, {
  eventName as WriteEventName,
} from '../schema/page/write';

const pageIdToSchemaMap = {
  [PAGE_CLASSIFY]: {
    getProperties: getClassifyProperties,
    name: ClassifyEventName,
  },
  [PAGE_SELECT_AD]: {
    getProperties: getSelectAdProperties,
    name: SelectAdEventName,
  },
  [PAGE_PAY_AND_POST]: {
    getProperties: getCheckoutProperties,
    name: CheckoutEventName,
  },
  [PAGE_PAY_AND_POST_V2]: {
    getProperties: getCheckoutProperties,
    name: CheckoutEventName,
  },
  [PAGE_MANAGE]: {
    getProperties: getManageProperties,
    name: ManageEventName,
  },
  [PAGE_CONCIERGE]: {
    getProperties: getBookingFormProperties,
    name: conciergeEventName,
  },
  [PAGE_GUARANTEED_HIRE]: {
    getProperties: getBookingFormProperties,
    name: guaranteedHireEventName,
  },
  [PAGE_PREMIUM_PLUS]: {
    getProperties: getBookingFormProperties,
    name: premiumPlusEventName,
  },
  [PAGE_WRITE]: {
    getProperties: getWriteProperties,
    name: WriteEventName,
  },
  [PAGE_MULTI_LOCATION]: {
    getProperties: getMultilocationProperties,
    name: MultilocationEventName,
  },
  [PAGE_EDIT]: {
    getProperties: getEditProperties,
    name: EditPageEventName,
  },
  [PAGE_EDIT_PAY_AND_POST]: {
    getProperties: getEditPayAndPostProperties,
    name: EditPayAndPostEventName,
  },
  [PAGE_SUCCESS]: {
    getProperties: getSuccessProperties,
    name: SuccessEventName,
  },
  [PAGE_REFRESH]: {
    getProperties: getRefreshProperties,
    name: RefreshEventName,
  },
  [PAGE_REFRESH_ERROR]: {
    getProperties: getRefreshErrorProperties,
    name: RefreshErrorEventName,
  },
};

const eventNameToSchemaMap = {
  [SELECT_AD_CONTINUE]: getSelectPageContinueProperties,
  [CLASSIFY_CONTINUE]: getClassifyContinueProperties,
  [WRITE_CONTINUE]: getWritePageContinueProperties,
  [PREMIUM_UPSELL_PRESSED]: getPremiumUpsellProperties,
  [CONTENT_ELEMENT_PRESSED]: getContentElementPressedProperties,
  [MANAGE_CONTINUE]: getManagePageContinueProperties,
};

type PageID = keyof typeof pageIdToSchemaMap;
type EventName = keyof typeof eventNameToSchemaMap;

/** @return global properties **/

export const getGlobalProperties = (tealium: Tealium) =>
  getGlobalAttributes(tealium);

// Returns data for the specific page
export const getPageProperties = (tealium: Tealium) => {
  const pageId = tealium.page;
  const pageSchema = pageIdToSchemaMap[pageId as PageID];
  if (!pageSchema) {
    log.warn('reducer | getPageProperties | Reducer for page not defined.', {
      pageId,
    });
    return {};
  }

  const data = pageSchema.getProperties(tealium);
  return data;
};

/** @return page event name **/

export const getPageEventName = (tealium: Tealium) => {
  const pageId = tealium.page;
  const pageSchema = pageIdToSchemaMap[pageId as PageID];
  if (!pageSchema) {
    log.warn('reducer | getPageEventName | Page name could not be found.', {
      pageId,
    });
    return;
  }
  return pageSchema.name;
};

/**  @return link properties  **/

export const getLinkProperties = (tealium: Tealium, propertyName?: string) => {
  if (!propertyName) {
    return {};
  }

  const getProperties = eventNameToSchemaMap[propertyName as EventName];

  return !getProperties ? {} : getProperties(tealium);
};
